import { Intent as BlueprintIntent, Callout, Classes } from '@blueprintjs/core';
import { DateRangeInput2 } from '@blueprintjs/datetime2';
import * as React from 'react';
import {
  Body,
  Button,
  LoadingIcon,
  Modal,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  VOICEBOX_RESPONSE_REPORT_CANCEL_BUTTON,
  VOICEBOX_RESPONSE_REPORT_DIALOG,
  VOICEBOX_RESPONSE_REPORT_END_DATE_INPUT,
  VOICEBOX_RESPONSE_REPORT_ERROR,
  VOICEBOX_RESPONSE_REPORT_START_DATE_INPUT,
  VOICEBOX_RESPONSE_REPORT_SUBMIT_BUTTON,
} from 'src/ui/constants/testIds';
import { useDownloadVoiceboxReport } from 'src/ui/containers/endpoints/utils/useDownloadVoiceboxReport';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import * as copy from 'src/ui/templates/copy';
import {
  GraphError,
  getErrorFromGraphQL,
} from 'src/ui/utils/getErrorFromGraphQL';

const DATE_INPUT_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

const formatDate = (date: Date | null) => {
  if (!date) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDate = (value: string) => {
  if (!value) {
    return null;
  }
  if (!DATE_INPUT_FORMAT.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }
  return date;
};

const getDefaultDateRange = (): [Date, Date] => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return [date, new Date()];
};

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error) {
    return getErrorFromGraphQL(error as GraphError).message;
  }
  if (error instanceof Error) return error.message;
  return copy.components.voiceboxResponseReport.genericError;
};

export const VoiceboxResponseReportDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const dialog = useAppSelector((state) => state.ui.dialog);
  const isOpen = dialog?.type === DialogType.VOICEBOX_RESPONSE_REPORT;
  const connection = isOpen ? dialog.data?.connection ?? null : null;
  const downloadVoiceboxReport = useDownloadVoiceboxReport(connection);

  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>(
    getDefaultDateRange
  );
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const reset = () => {
    setDateRange(getDefaultDateRange());
    setError('');
    setIsLoading(false);
  };

  const handleClose = () => {
    dispatch(closedDialog());
    reset();
  };

  const validate = () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) {
      return copy.components.voiceboxResponseReport.missingDates;
    }
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError || !connection) {
      setError(validationError);
      return;
    }
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) {
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      await downloadVoiceboxReport(formatDate(startDate), formatDate(endDate));
      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message: copy.components.voiceboxResponseReport.success,
        })
      );
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
      setIsLoading(false);
    }
  };

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            disabled={isLoading}
            onClick={handleClose}
            size="sm"
            testId={VOICEBOX_RESPONSE_REPORT_CANCEL_BUTTON}
            variant="ghost-gray"
          >
            {copy.components.voiceboxResponseReport.cancel}
          </Button>
          <Button
            disabled={isLoading}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={VOICEBOX_RESPONSE_REPORT_SUBMIT_BUTTON}
            variant="primary-accent"
          >
            {copy.components.voiceboxResponseReport.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      testId={VOICEBOX_RESPONSE_REPORT_DIALOG}
      title={copy.components.voiceboxResponseReport.title}
    >
      <div className="endpoint-dialog-body">
        <Body size="md">
          {copy.components.voiceboxResponseReport.description}
        </Body>
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <div className="endpoint-dialog-date-range">
            <div className="endpoint-dialog-date-range__labels">
              <label
                className={Classes.LABEL}
                htmlFor={VOICEBOX_RESPONSE_REPORT_START_DATE_INPUT}
              >
                {copy.components.voiceboxResponseReport.startDate}
              </label>
              <label
                className={Classes.LABEL}
                htmlFor={VOICEBOX_RESPONSE_REPORT_END_DATE_INPUT}
              >
                {copy.components.voiceboxResponseReport.endDate}
              </label>
            </div>
            <DateRangeInput2
              allowSingleDayRange
              disabled={isLoading}
              fill
              formatDate={formatDate}
              onChange={setDateRange}
              parseDate={parseDate}
              shortcuts={false}
              singleMonthOnly
              startInputProps={{
                id: VOICEBOX_RESPONSE_REPORT_START_DATE_INPUT,
              }}
              endInputProps={{
                id: VOICEBOX_RESPONSE_REPORT_END_DATE_INPUT,
              }}
              value={dateRange}
            />
          </div>
          <input hidden type="submit" />
        </form>
        {error ? (
          <Callout
            data-testid={VOICEBOX_RESPONSE_REPORT_ERROR}
            icon={null}
            intent={BlueprintIntent.DANGER}
          >
            {error}
          </Callout>
        ) : null}
      </div>
    </Modal>
  );
};
